import { getClientStorage, setClientStorage } from '@/figma/clientstorage';

const flattenGroup = (group) => {
  const groups = [];

  if ('variables' in group) groups.push(group);
  if ('variableGroups' in group)
    group.variableGroups.flatMap(flattenGroup).forEach((group) => groups.push(group));

  return groups;
};

export const variableStore = {
  namespaced: true,

  state: {
    isFirstLoad: true,
    isLoading: true,
    variablesV2AnnouncementRead: false,

    libraries: [],
    selectedVariableIds: new Set(),
    selectedModeIds: new Set(),
  },

  getters: {
    getVariableMap: (state, getters) => (libraries) =>
      new Map(
        libraries.flatMap((lib) =>
          lib.variableCollections.flatMap((collection) => {
            const groups = getters.getFlattenedGroups(collection);
            const variables = groups.flatMap((group) => group.variables);

            return variables.map((variable) => [variable.id, variable]);
          })
        )
      ),
    variableMap: (state, getters) => getters.getVariableMap(state.libraries),
    linkedVariableIds: (state, getters) => {
      const selectedVariableIds = Array.from(state.selectedVariableIds);
      const linkedVariableIds = new Set();

      for (const id of selectedVariableIds) {
        const variable = getters.variableMap.get(id);
        if (!variable) continue;

        const originalVariableIds = new Set(
          variable.modes
            .filter((mode) => mode.value.type === 'VARIABLE_ALIAS')
            .map((mode) => mode.value.parentVariableId)
        );

        originalVariableIds.forEach((id) => linkedVariableIds.add(id));
      }

      return linkedVariableIds;
    },
    getVariableIdsFromGroup: () => (group) => group.variables.map((variable) => variable.id),
    isVariableSelected: (state) => (id) => state.selectedVariableIds.has(id),

    getFlattenedGroups: () => (collection) =>
      flattenGroup(collection).flatMap((group) => ({
        ...group,
        variables: group.variables.map((variable) => ({
          ...variable,
          name: variable.name.split('/').pop(),
        })),
      })),
    getGroupStatus: (state) => (group) => {
      const quantity = group.variables.reduce(
        (acc, variable) => (state.selectedVariableIds.has(variable.id) ? acc + 1 : acc),
        0
      );

      return quantity === group.variables.length
        ? 'selected'
        : quantity > 0
          ? 'mixed'
          : 'unselected';
    },
    getGroupsStatus: (state, getters) => (groups) => {
      const statuses = new Set(groups.map((group) => getters.getGroupStatus(group)));
      return statuses.size > 1 ? 'mixed' : statuses.values().next().value;
    },

    getModeMap: () => (libraries) =>
      new Map(
        libraries.flatMap((lib) =>
          lib.variableCollections.flatMap((collection) =>
            collection.modes.map((mode) => [
              mode.compositeId,
              { mode, collectionId: collection.id },
            ])
          )
        )
      ),
    modeMap: (state, getters) => getters.getModeMap(state.libraries),
    getModeStatus: (state) => (id) => (state.selectedModeIds.has(id) ? 'selected' : 'unselected'),
    getModesStatus: (state) => (modeIds) => {
      const quantity = modeIds.reduce(
        (acc, id) => (state.selectedModeIds.has(id) ? acc + 1 : acc),
        0
      );

      return quantity === modeIds.length ? 'selected' : quantity > 0 ? 'mixed' : 'unselected';
    },

    collectionMap: (state) =>
      new Map(
        state.libraries.flatMap((lib) =>
          lib.variableCollections.map((collection) => [
            collection.id,
            { collection, libraryId: lib.id },
          ])
        )
      ),

    getCollectionStatus: (state, getters) => (collection) => {
      const groups = getters.getFlattenedGroups(collection);
      const groupsStatus = getters.getGroupsStatus(groups);
      const modesStatus = getters.getModesStatus(collection.modes.map((mode) => mode.compositeId));
      const statuses = new Set([groupsStatus, modesStatus]);

      return statuses.size > 1 ? 'mixed' : statuses.values().next().value;
    },

    getLibraryStatus: (state, getters) => (library) => {
      const statuses = new Set(
        library.variableCollections.map((collection) => getters.getCollectionStatus(collection))
      );

      return statuses.size > 1 ? 'mixed' : statuses.values().next().value;
    },

    selectedCollections: (state, getters) =>
      state.libraries.flatMap((lib) =>
        lib.variableCollections
          .filter((collection) => getters.getCollectionStatus(collection) !== 'unselected')
          .map((collection) => ({
            ...collection,
            libraryId: lib.id,
            libraryName: lib.name,
          }))
      ),

    selectedModes: (_, getters) =>
      getters.selectedCollections.flatMap((collection) =>
        collection.modes
          .filter((mode) => getters.getModeStatus(mode.compositeId) !== 'unselected')
          .map((mode) => ({
            ...mode,
            collectionId: collection.id,
          }))
      ),

    selectedGroups: (_, getters) =>
      getters.selectedCollections.flatMap((collection) =>
        getters
          .getFlattenedGroups(collection)
          .filter((group) => getters.getGroupStatus(group) !== 'unselected')
          .map((group) => ({
            ...group,
            collectionId: collection.id,
          }))
      ),

    selectedVariables: (_, getters) => {
      return getters.selectedGroups.flatMap((group) =>
        group.variables
          .filter((variable) => getters.isVariableSelected(variable.id))
          .map((variable) => ({
            ...variable,
            modes: variable.modes.filter(
              (mode) => getters.getModeStatus(mode.compositeId) !== 'unselected'
            ),
            groupId: group.id,
            collectionId: group.collectionId,
          }))
      );
    },

    isSyncEnabled: (state, getters) => {
      return (
        !state.isLoading &&
        (getters.selectedVariables.length > 0 || getters.selectedModes.length > 0)
      );
    },
  },

  mutations: {
    setIsLoading(state, isLoading) {
      state.isLoading = isLoading;
    },

    setVariablesV2AnnouncementRead(state, value) {
      state.variablesV2AnnouncementRead = value;
    },

    setLibraries(state, libraries) {
      state.libraries = libraries;
    },

    selectVariables(state, ids) {
      ids.forEach((id) => state.selectedVariableIds.add(id));
    },

    unselectVariables(state, ids) {
      ids.forEach((id) => state.selectedVariableIds.delete(id));
    },

    toggleVariable(state, id) {
      state.selectedVariableIds.has(id)
        ? state.selectedVariableIds.delete(id)
        : state.selectedVariableIds.add(id);
    },

    selectModes(state, ids) {
      ids.forEach((id) => state.selectedModeIds.add(id));
    },

    unselectModes(state, ids) {
      ids.forEach((id) => state.selectedModeIds.delete(id));
    },

    toggleMode(state, id) {
      state.selectedModeIds.has(id)
        ? state.selectedModeIds.delete(id)
        : state.selectedModeIds.add(id);
    },
  },

  actions: {
    async loadData({ commit }) {
      const selectedVariableIds = await getClientStorage('selectedVariableIds');
      if (selectedVariableIds) commit('selectVariables', selectedVariableIds);

      const selectedModeIds = await getClientStorage('selectedModeIds');
      if (selectedModeIds) commit('selectModes', selectedModeIds);

      const libraries = await getClientStorage('libraries');
      if (libraries) {
        commit('setLibraries', libraries);
        commit('setIsLoading', false);
      }
    },

    updateLibraries({ commit, dispatch, getters }, { libraries }) {
      const collections = libraries.flatMap((lib) => lib.variableCollections);
      const groups = collections.flatMap((collection) => getters.getFlattenedGroups(collection));

      const newVariables = getters.getVariableMap(libraries);
      const oldVariables = getters.variableMap;
      const diffVariables = [...newVariables.values()].filter(
        (variable) => !oldVariables.has(variable.id)
      );

      diffVariables.length &&
        groups.forEach((group) => {
          const prevGroup = {
            ...group,
            variables: group.variables.filter((v) => !diffVariables.some((dv) => dv.id === v.id)),
          };

          const status = getters.getGroupStatus(prevGroup);

          for (const variable of [...diffVariables]) {
            const isVariableInGroup = group.variables.some((v) => v.id === variable.id);

            if (!isVariableInGroup) break;

            if (status === 'selected') {
              commit('selectVariables', [variable.id]);
            }

            diffVariables.shift();
          }
        });

      const newModes = getters.getModeMap(libraries);
      const oldModes = getters.modeMap;
      const diffModes = [...newModes.values()].filter(
        ({ mode }) => !oldModes.has(mode.compositeId)
      );

      diffModes.length &&
        collections.forEach((collection) => {
          const modes = collection.modes.filter(
            (mode) => !diffModes.some((dv) => dv.mode.compositeId === mode.compositeId)
          );

          const status = getters.getModesStatus(modes.map((mode) => mode.compositeId));

          for (const { mode } of [...diffModes]) {
            const isModeInCollection = collection.modes.some(
              (m) => m.compositeId === mode.compositeId
            );

            if (!isModeInCollection) break;

            if (status === 'selected') {
              commit('selectModes', [mode.compositeId]);
            }

            diffModes.shift();
          }
        });

      commit('setLibraries', libraries);
      commit('setIsLoading', false);

      dispatch('storeLibraries');
      dispatch('storeSelectedVariableIds');
      dispatch('storeSelectedModeIds');
    },

    storeLibraries({ state }) {
      const libraries = JSON.parse(JSON.stringify(state.libraries));
      return setClientStorage('libraries', libraries);
    },

    storeSelectedVariableIds({ state }) {
      return setClientStorage('selectedVariableIds', [...state.selectedVariableIds]);
    },

    storeSelectedModeIds({ state }) {
      return setClientStorage('selectedModeIds', [...state.selectedModeIds]);
    },

    selectVariables({ commit, dispatch, getters }, ids) {
      const defaultModeIds = new Set(
        ids.map((id) => getters.variableMap.get(id).modes[0].compositeId)
      );

      commit('selectVariables', ids);
      dispatch('selectModes', [...defaultModeIds]);
      return dispatch('storeSelectedVariableIds');
    },

    unselectVariables({ commit, dispatch }, ids) {
      commit('unselectVariables', ids);
      return dispatch('storeSelectedVariableIds');
    },

    toggleVariable({ commit, dispatch, getters }, id) {
      const variable = getters.variableMap.get(id);
      const defaultModeId = variable.modes[0].compositeId;

      commit('toggleVariable', id);

      const { collectionId } = getters.modeMap.get(defaultModeId);
      const { collection } = getters.collectionMap.get(collectionId);
      const groups = getters.getFlattenedGroups(collection);
      const groupsSelected = getters.getGroupsStatus(groups) !== 'unselected';

      if (!groupsSelected && variable.modes.length === 1) {
        commit('unselectModes', [defaultModeId]);
      } else {
        commit('selectModes', [defaultModeId]);
      }

      return dispatch('storeSelectedVariableIds');
    },

    selectModes({ commit, dispatch }, ids) {
      commit('selectModes', ids);
      return dispatch('storeSelectedModeIds');
    },

    unselectModes({ commit, dispatch }, ids) {
      commit('unselectModes', ids);
      return dispatch('storeSelectedModeIds');
    },

    toggleMode({ commit, dispatch, getters }, id) {
      const { collectionId } = getters.modeMap.get(id);
      const { collection } = getters.collectionMap.get(collectionId);
      const defaultModeId = collection.modes[0].compositeId;

      commit('toggleMode', id);
      dispatch('selectModes', [defaultModeId]);
      return dispatch('storeSelectedModeIds');
    },

    toggleModes({ commit, dispatch, getters }, ids) {
      const status = getters.getModesStatus(ids);
      const mutation = status === 'selected' ? 'unselectModes' : 'selectModes';
      commit(mutation, status === 'selected' ? ids.slice(1) : ids);
      return dispatch('storeSelectedModeIds');
    },

    selectGroup({ dispatch, getters }, group) {
      const ids = getters.getVariableIdsFromGroup(group);
      dispatch('selectVariables', ids);
    },

    unselectGroup({ dispatch, getters }, group) {
      const ids = getters.getVariableIdsFromGroup(group);
      dispatch('unselectVariables', ids);
    },

    toggleGroup({ dispatch, getters }, group) {
      const status = getters.getGroupStatus(group);
      const ids = getters.getVariableIdsFromGroup(group);
      const modes = group.variables[0].modes;

      if (modes.length === 1) {
        dispatch('unselectModes', [modes[0].compositeId]);
      }

      dispatch(status === 'selected' ? 'unselectVariables' : 'selectVariables', ids);
    },

    toggleGroups({ dispatch, getters }, groups) {
      const groupStatuses = new Set(groups.map((group) => getters.getGroupStatus(group)));
      const status = groupStatuses.size > 1 ? 'mixed' : groupStatuses.values().next().value;

      groups.forEach((group) => {
        status === 'selected' ? dispatch('unselectGroup', group) : dispatch('selectGroup', group);
      });
    },

    selectCollection({ commit, dispatch, getters }, collection) {
      const groups = getters.getFlattenedGroups(collection);
      const modeIds = collection.modes.map((mode) => mode.compositeId);

      groups.forEach((group) => dispatch('selectGroup', group));
      commit('selectModes', modeIds);
    },

    unselectCollection({ commit, dispatch, getters }, collection) {
      const groups = getters.getFlattenedGroups(collection);
      const modeIds = collection.modes.map((mode) => mode.compositeId);

      groups.forEach((group) => dispatch('unselectGroup', group));
      commit('unselectModes', modeIds);
    },

    toggleCollection: ({ commit, dispatch, getters }, collection) => {
      const status = getters.getCollectionStatus(collection);
      const groups = getters.getFlattenedGroups(collection);
      const modeIds = collection.modes.map((mode) => mode.compositeId);

      groups.forEach((group) => {
        if (status === 'selected') {
          dispatch('unselectGroup', group);
          commit('unselectModes', modeIds);
          return;
        }

        groups.forEach((group) => {
          dispatch('selectGroup', group);
          commit('selectModes', modeIds);
        });
      });
    },

    toggleLibrary({ dispatch, getters }, library) {
      const status = getters.getLibraryStatus(library);

      library.variableCollections.forEach((collection) => {
        dispatch(status === 'selected' ? 'unselectCollection' : 'selectCollection', collection);
      });
    },
  },
};
